import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import WebcamCapture from '../WebcamCapture';
import { isValidImageFormat, createImageData } from '../verificationUtils';
import { validateIdDocument } from '../documentValidation';

/**
 * DocumentCaptureStep - Second step for capturing document ID
 * Sequential flow with confirmation:
 * 1. Capture front
 * 2. Show confirmation with check + message about needing the back
 * 3. User presses "Continuar" to proceed to back capture
 * 4. Capture back
 * 
 * @param {Object} props
 * @param {Object} props.documentFront - Front image data
 * @param {Object} props.documentBack - Back image data
 * @param {Function} props.onDocumentFrontCapture - Callback for front capture
 * @param {Function} props.onDocumentBackCapture - Callback for back capture
 * @param {Function} props.onContinue - Callback when all images captured
 * @param {Function} props.onError - Callback for errors
 * @param {boolean} props.requireDocumentBack - Whether back side is required (default: true)
 */
function DocumentCaptureStep({
  documentFront,
  documentBack,
  onDocumentFrontCapture,
  onDocumentBackCapture,
  onContinue,
  onError,
  requireDocumentBack = true,
  isProcessing = false
}) {
  const { t } = useTranslation();
  const [showCamera, setShowCamera] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Track whether user has acknowledged the front and is ready for back
  const [readyForBack, setReadyForBack] = useState(false);
  const fileInputRef = useRef(null);

  // Determine which phase we're in
  // 'front' - capturing front
  // 'front-done' - front captured, showing confirmation (only if back is required)
  // 'back' - capturing back
  // 'done' - all required images captured
  let phase;
  if (!documentFront) {
    phase = 'front';
  } else if (requireDocumentBack && !readyForBack) {
    phase = 'front-done';
  } else if (requireDocumentBack && !documentBack) {
    phase = 'back';
  } else {
    phase = 'done';
  }

  // Scroll to top when phase changes
  useEffect(() => {
    const modalContent = document.querySelector('.verification-modal-content');
    if (modalContent) {
      modalContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setShowCamera(false);
  }, [phase]);

  // Reset readyForBack when front is cleared
  useEffect(() => {
    if (!documentFront) {
      setReadyForBack(false);
    }
  }, [documentFront]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isValidImageFormat(file.type, file.name)) {
      if (onError) {
        onError(new Error(
          t('verification.invalid-file-format', {
            defaultValue: 'Formato de archivo inválido. Por favor sube una imagen PNG o JPEG.'
          })
        ));
      }
      event.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target.result;
        const validation = await validateIdDocument(dataUrl);

        if (!validation.isValid) {
          setIsUploading(false);
          if (onError) {
            onError(new Error(
              validation.reason ||
              t('verification.invalid-document', {
                defaultValue: 'La imagen no parece ser un documento de identidad válido. Por favor, asegúrate de subir un documento oficial con buena iluminación y enfoque.'
              })
            ));
          }
          return;
        }

        const imageData = createImageData({
          dataUrl,
          name: file.name,
          type: file.type,
          size: file.size
        });

        if (phase === 'front') {
          onDocumentFrontCapture(imageData);
        } else {
          onDocumentBackCapture(imageData);
        }
        setIsUploading(false);
      };
      reader.onerror = () => {
        setIsUploading(false);
        if (onError) {
          onError(new Error(
            t('verification.file-read-error', {
              defaultValue: 'Error al leer el archivo. Por favor intenta de nuevo.'
            })
          ));
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setIsUploading(false);
      if (onError) {
        onError(error);
      }
    }

    event.target.value = '';
  };

  const handleWebcamCapture = async (imageData) => {
    try {
      const validation = await validateIdDocument(imageData.dataUrl);

      if (!validation.isValid) {
        if (onError) {
          onError(new Error(
            validation.reason ||
            t('verification.invalid-document', {
              defaultValue: 'La imagen no parece ser un documento de identidad válido. Por favor, asegúrate de capturar un documento oficial con buena iluminación y enfoque.'
            })
          ));
        }
        return;
      }

      if (phase === 'front') {
        onDocumentFrontCapture(imageData);
      } else {
        onDocumentBackCapture(imageData);
      }
      setShowCamera(false);
    } catch (error) {
      console.error('Document validation error:', error);
      if (onError) {
        onError(new Error(
          t('verification.validation-error', {
            defaultValue: 'Error al validar el documento. Por favor intenta nuevamente.'
          })
        ));
      }
    }
  };

  // Confirmation screen after front is captured (when back is required)
  if (phase === 'front-done') {
    return (
      <div className="space-y-6">
        {/* Success indicator */}
        <div className="flex flex-col items-center text-center py-4">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('verification.front-captured-success', {
              defaultValue: 'Frente del documento capturado'
            })}
          </h3>
          <p className="text-sm text-gray-600">
            {t('verification.now-capture-back', {
              defaultValue: 'Ahora necesitas capturar el reverso de tu documento de identidad.'
            })}
          </p>
        </div>

        {/* Thumbnail of front */}
        <div className="relative">
          <img
            src={documentFront.dataUrl}
            alt="Document front"
            className="w-full max-h-36 object-contain rounded-lg border border-green-300 bg-green-50"
          />
          <button
            type="button"
            className="absolute top-2 right-2 amplify-button amplify-button--small bg-white"
            onClick={() => onDocumentFrontCapture(null)}
          >
            {t('verification.retake-button', { defaultValue: 'Volver a capturar' })}
          </button>
        </div>

        {/* Continue button to proceed to back capture */}
        <button
          type="button"
          className="amplify-button amplify-button--primary w-full py-3 flex items-center justify-center gap-2"
          onClick={() => setReadyForBack(true)}
        >
          {t('verification.continue-to-back', {
            defaultValue: 'Continuar — Capturar reverso'
          })}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
  }

  // Summary screen when all images are captured
  if (phase === 'done') {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center text-center py-2">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {t('verification.document-capture-title', {
              defaultValue: 'Documento capturado'
            })}
          </h3>
          <p className="text-sm text-green-700 font-medium">
            {requireDocumentBack
              ? t('verification.document-both-captured', { defaultValue: 'Ambos lados del documento han sido capturados correctamente.' })
              : t('verification.document-front-captured', { defaultValue: 'El documento ha sido capturado correctamente.' })}
          </p>
        </div>

        {/* Front preview */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-gray-500 uppercase">
            {t('verification.document-front-title', { defaultValue: 'Frente' })}
          </h4>
          <div className="relative">
            <img
              src={documentFront.dataUrl}
              alt="Document front"
              className="w-full max-h-32 object-contain rounded-lg border border-gray-300 bg-gray-50"
            />
            <button
              type="button"
              className="absolute top-2 right-2 amplify-button amplify-button--small bg-white"
              onClick={() => onDocumentFrontCapture(null)}
            >
              {t('verification.retake-button', { defaultValue: 'Volver a capturar' })}
            </button>
          </div>
        </div>

        {/* Back preview */}
        {requireDocumentBack && documentBack && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase">
              {t('verification.document-back-title', { defaultValue: 'Reverso' })}
            </h4>
            <div className="relative">
              <img
                src={documentBack.dataUrl}
                alt="Document back"
                className="w-full max-h-32 object-contain rounded-lg border border-gray-300 bg-gray-50"
              />
              <button
                type="button"
                className="absolute top-2 right-2 amplify-button amplify-button--small bg-white"
                onClick={() => onDocumentBackCapture(null)}
              >
                {t('verification.retake-button', { defaultValue: 'Volver a capturar' })}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Capture screen (front or back)
  const currentImage = phase === 'front' ? documentFront : documentBack;
  const clearCurrentImage = () => {
    if (phase === 'front') {
      onDocumentFrontCapture(null);
    } else {
      onDocumentBackCapture(null);
    }
  };

  const title = phase === 'front'
    ? t('verification.document-front-title', { defaultValue: 'Frente del documento' })
    : t('verification.document-back-title', { defaultValue: 'Reverso del documento' });

  const instructions = phase === 'front'
    ? t('verification.document-capture-instructions-front-only', {
        defaultValue: 'Por favor proporciona una imagen clara del frente de tu documento de identidad oficial (pasaporte, licencia de conducir o cédula de identidad).'
      })
    : t('verification.document-back-instructions', {
        defaultValue: 'Captura una imagen clara del reverso de tu documento de identidad.'
      });

  return (
    <div className="space-y-6 relative">
      {/* Loading overlay */}
      {isUploading && (
        <div className="absolute inset-0 bg-white/80 z-10 flex flex-col items-center justify-center rounded-lg">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent mb-3"></div>
          <p className="text-sm text-gray-600">
            {t('verification.processing-document', { defaultValue: 'Procesando documento...' })}
          </p>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600">{instructions}</p>
      </div>

      {/* Capture area */}
      <div className="space-y-3">
        {!showCamera && !currentImage && (
          <div className="flex gap-2">
            <button
              type="button"
              className="amplify-button amplify-button--outlined flex-1 flex items-center justify-center gap-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {t('verification.upload-button', { defaultValue: 'Subir' })}
            </button>
            <button
              type="button"
              className="amplify-button amplify-button--primary flex-1 flex items-center justify-center gap-2"
              onClick={() => setShowCamera(true)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('verification.camera-button', { defaultValue: 'Cámara' })}
            </button>
          </div>
        )}

        {showCamera && (
          <WebcamCapture
            onCapture={handleWebcamCapture}
            onError={onError}
            mirrored={false}
            facingMode="environment"
          />
        )}

        {currentImage && !showCamera && (
          <div className="relative">
            <img
              src={currentImage.dataUrl}
              alt={phase === 'front' ? 'Document front' : 'Document back'}
              className="w-full max-h-48 object-contain rounded-lg border border-gray-300 bg-gray-50"
            />
            <button
              type="button"
              className="absolute top-2 right-2 amplify-button amplify-button--small bg-white"
              onClick={clearCurrentImage}
            >
              {t('verification.retake-button', { defaultValue: 'Volver a capturar' })}
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          className="hidden"
          onChange={handleFileUpload}
        />
      </div>
    </div>
  );
}

export default DocumentCaptureStep;
